/**
 * One record of what the visitor agreed to, and one place that reads it.
 *
 * This module gates two HighLevel (LeadConnector) tools: the website chat
 * widget and the booking calendar iframe
 * with its resizer script. Both run on hosts we do not control and both write
 * to the visitor's browser, so both wait for a choice. The site loads no
 * analytics or advertising product of its own, so there is no analytics
 * vendor category to ask about. Version 3 adds a separate opt-in for a local,
 * short-lived inquiry-source note, sent only with a form submission.
 * The existing Google Preferred Sources loader is separate and disclosed in
 * the cookie policy; it is not controlled by these two preferences.
 *
 * Version 1 of this record (`{ essentials, analytics, ts }`) is not readable
 * here on purpose. It was written by a banner that described a category the
 * site never had, and it was stored while the chat widget loaded regardless of
 * the answer. Neither a v1 acceptance nor a v1 rejection is a decision about
 * what version 2 actually gates, so a v1 record reads as "no decision yet" and
 * the visitor is asked once more.
 */

export const CONSENT_STORAGE_KEY = "cookie-consent";
export const CONSENT_VERSION = 3;

/** Fired on window whenever a decision is stored. */
export const CONSENT_CHANGE_EVENT = "chleb:consent-change";
/** Fired on window to reopen the banner (footer link, cookie policy button). */
export const OPEN_COOKIE_SETTINGS_EVENT = "open-cookie-settings";

export interface ConsentRecord {
  version: number;
  /** Always true. Named so the stored record explains itself when read raw. */
  essential: true;
  /** The HighLevel chat widget and booking calendar. */
  embeds: boolean;
  /** Optional tab-local entry context, never implied by a legacy embed choice. */
  inquirySource: boolean;
  /** ISO 8601, so a stored record says when the choice was made. */
  decidedAt: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function readConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    if (parsed.version !== CONSENT_VERSION && parsed.version !== 2) return null;
    if (typeof parsed.embeds !== "boolean") return null;
    return {
      version: parsed.version,
      essential: true,
      embeds: parsed.embeds,
      inquirySource: parsed.version === CONSENT_VERSION && parsed.inquirySource === true,
      decidedAt:
        typeof parsed.decidedAt === "string" ? parsed.decidedAt : "",
    };
  } catch {
    // Storage can be unavailable in hardened or private browser contexts, and
    // an unreadable record is the same as no record: ask again, load nothing.
    return null;
  }
}

/** True only when a recognized stored record says yes to embeds. */
export function embedsAllowed(): boolean {
  return readConsent()?.embeds === true;
}

export function globalPrivacyControlSet(): boolean {
  return typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

export function inquirySourceAllowed(): boolean {
  return !globalPrivacyControlSet() && readConsent()?.inquirySource === true;
}

export function writeConsent(embeds: boolean, inquirySource = false): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    essential: true,
    embeds,
    inquirySource: inquirySource && !globalPrivacyControlSet(),
    decidedAt: new Date().toISOString(),
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify(record),
      );
    } catch {
      // A choice that cannot be stored still applies to this page view: the
      // event below is dispatched either way, and the banner returns next
      // visit because nothing was written.
    }
    window.dispatchEvent(
      new CustomEvent<ConsentRecord>(CONSENT_CHANGE_EVENT, { detail: record }),
    );
  }
  return record;
}

export function onConsentChange(
  listener: (record: ConsentRecord) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<ConsentRecord>).detail;
    if (detail) listener(detail);
  };
  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}

export function openCookieSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}
